/**
 * 通知のまとめ方。
 *
 * Supabase を参照する処理(→ notifications.ts)とは分けてある。
 * ここだけならそのままテストできる。
 */

export type NotificationKind =
  | "media_added"
  | "media_deleted"
  | "comment_added"
  | "event_added"
  | "diary_entry_added"
  | "profile_changed";

export interface NotificationRow {
  id: string;
  kind: NotificationKind;
  actor_id: string;
  media_id: string | null;
  body: string | null;
  created_at: string;
  read_at: string | null;
  event_id: string | null;
}

export interface NotificationEntry {
  /** 代表の行のID。並べ替えや key に使う */
  id: string;
  /** 畳んだ元の行すべて。既読を付けるときはこの全部を対象にする */
  rowIds: string[];
  kind: NotificationKind;
  actorId: string;
  createdAt: string;
  unread: boolean;
  /** まとめた件数。1件なら1 */
  count: number;
  mediaIds: string[];
  body: string | null;
  eventId: string | null;
}

/** ひとまとめにする時間の幅。1回のアップロードは大抵この中に収まる */
export const GROUP_WINDOW_MS = 60 * 60 * 1000;

/** まとめてよい種類。コメントは1つずつ読みたいのでまとめない */
function groupable(kind: NotificationKind): boolean {
  return kind === "media_added" || kind === "media_deleted" || kind === "profile_changed";
}

/**
 * 12件まとめて上げたときに12行並べても仕方がないので、
 * 同じ人・同じ種類が続くぶんを1件に畳む。
 *
 * `rows` は新しい順に並んでいる前提。
 */
export function groupNotifications(
  rows: readonly NotificationRow[],
  windowMs = GROUP_WINDOW_MS,
): NotificationEntry[] {
  const entries: NotificationEntry[] = [];

  for (const row of rows) {
    const last = entries.at(-1);
    const sameRun =
      last !== undefined &&
      groupable(row.kind) &&
      last.kind === row.kind &&
      last.actorId === row.actor_id &&
      new Date(last.createdAt).getTime() - new Date(row.created_at).getTime() < windowMs;

    if (sameRun && last !== undefined) {
      last.count += 1;
      last.rowIds.push(row.id);
      last.unread = last.unread || row.read_at === null;
      if (row.media_id !== null) last.mediaIds.push(row.media_id);
      continue;
    }

    entries.push({
      id: row.id,
      rowIds: [row.id],
      kind: row.kind,
      actorId: row.actor_id,
      createdAt: row.created_at,
      unread: row.read_at === null,
      count: 1,
      mediaIds: row.media_id === null ? [] : [row.media_id],
      body: row.body,
      eventId: row.event_id,
    });
  }

  return entries;
}

/** 未読の件数。畳んだ後の数を数える(12件上げても「1」と出したい) */
export function unreadCountOf(entries: readonly NotificationEntry[]): number {
  return entries.filter((entry) => entry.unread).length;
}

/** 「さらが写真・動画を12件追加しました」のような一行 */
export function describeEntry(entry: NotificationEntry, actorName: string): string {
  const who = actorName === "" ? "相手" : actorName;
  if (entry.kind === "comment_added") {
    return `${who}がコメントしました`;
  }
  if (entry.kind === "event_added") {
    return `${who}が予定を追加しました`;
  }
  if (entry.kind === "diary_entry_added") {
    return `${who}が交換日記を書きました`;
  }
  if (entry.kind === "profile_changed") {
    if (entry.count > 1) {
      return `${who}がプロフィールを${String(entry.count)}件更新しました`;
    }
    return `${who}が${entry.body ?? "プロフィール"}を変更しました`;
  }
  const what = entry.kind === "media_added" ? "追加" : "削除";
  return `${who}が写真・動画を${String(entry.count)}件${what}しました`;
}

/** 「3分前」「2時間前」、7日以降は「2026年08月13日」(年が無いと昨年の通知と紛れる) */
export function relativeTimeOf(isoTimestamp: string, now: Date = new Date()): string {
  const elapsed = now.getTime() - new Date(isoTimestamp).getTime();
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "たった今";
  if (minutes < 60) return `${String(minutes)}分前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}時間前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${String(days)}日前`;
  const date = new Date(isoTimestamp);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${String(date.getFullYear())}年${pad(date.getMonth() + 1)}月${pad(date.getDate())}日`;
}
